/**
 * Service for tracking in-game date and time using the Julian calendar.
 *
 * The Julian calendar is appropriate for the game's 1500 AD setting.
 * Time is driven by server ticks, with configurable conversion rate.
 *
 * Uses incremental tracking for O(1) update and query performance.
 */

// ---------------------------------------------------------------------------
// Configuration Constants
// ---------------------------------------------------------------------------

/** Number of server ticks that equal 1 in-game minute. */
export const TICKS_PER_MINUTE = 10;

/** Starting year (AD). */
export const STARTING_YEAR = 1500;

/** Starting month (1-12). */
export const STARTING_MONTH = 2; // February

/** Starting day of month (1-31). */
export const STARTING_DAY = 28;

/** Starting hour (0-23). */
export const STARTING_HOUR = 6;

/** Starting minute (0-59). */
export const STARTING_MINUTE = 0;

// ---------------------------------------------------------------------------
// Julian Calendar Helpers
// ---------------------------------------------------------------------------

/** Days in each month for a non-leap year. Index 0 is unused for 1-based months. */
const DAYS_IN_MONTH = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** Month names for formatting. */
const MONTH_NAMES = [
  '',
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * Check if a year is a leap year in the Julian calendar.
 * Julian rule: divisible by 4.
 */
function isJulianLeapYear(year: number): boolean {
  return year % 4 === 0;
}

/**
 * Get the number of days in a given month for a given year (Julian calendar).
 */
function getDaysInMonth(year: number, month: number): number {
  if (month === 2 && isJulianLeapYear(year)) {
    return 29;
  }
  return DAYS_IN_MONTH[month];
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Represents an in-game date and time. */
export interface GameDateTime {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  hour: number; // 0-23
  minute: number; // 0-59
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

/**
 * Tracks in-game date and time using incremental updates.
 *
 * Call `update()` each server tick to advance time.
 * Use getter methods to retrieve current in-game date/time in O(1).
 */
export class GameTimeService {
  private tickAccumulator: number = 0;
  private year: number = STARTING_YEAR;
  private month: number = STARTING_MONTH;
  private day: number = STARTING_DAY;
  private hour: number = STARTING_HOUR;
  private minute: number = STARTING_MINUTE;

  /**
   * Called each server tick to advance in-game time.
   * Increments tick accumulator and carries over to minutes/hours/days/months/years.
   */
  public update(): void {
    this.tickAccumulator++;

    if (this.tickAccumulator >= TICKS_PER_MINUTE) {
      this.tickAccumulator = 0;
      this.advanceMinute();
    }
  }

  /**
   * Advance time by one minute, handling all carry-overs.
   */
  private advanceMinute(): void {
    this.minute++;

    if (this.minute >= 60) {
      this.minute = 0;
      this.advanceHour();
    }
  }

  /**
   * Advance time by one hour, handling carry-over to days.
   */
  private advanceHour(): void {
    this.hour++;

    if (this.hour >= 24) {
      this.hour = 0;
      this.advanceDay();
    }
  }

  /**
   * Advance time by one day, handling carry-over to months/years.
   */
  private advanceDay(): void {
    this.day++;

    const daysInCurrentMonth = getDaysInMonth(this.year, this.month);
    if (this.day > daysInCurrentMonth) {
      this.day = 1;
      this.advanceMonth();
    }
  }

  /**
   * Advance time by one month, handling carry-over to years.
   */
  private advanceMonth(): void {
    this.month++;

    if (this.month > 12) {
      this.month = 1;
      this.year++;
    }
  }

  /**
   * Get the current in-game date and time. O(1) operation.
   */
  public getDateTime(): GameDateTime {
    return {
      year: this.year,
      month: this.month,
      day: this.day,
      hour: this.hour,
      minute: this.minute,
    };
  }

  /**
   * Get a human-readable date string (e.g., "28 February 1500").
   */
  public getFormattedDate(): string {
    return `${this.day} ${MONTH_NAMES[this.month]} ${this.year}`;
  }

  /**
   * Get a time string (e.g., "14:30").
   */
  public getFormattedTime(): string {
    const hh = this.hour.toString().padStart(2, '0');
    const mm = this.minute.toString().padStart(2, '0');
    return `${hh}:${mm}`;
  }

  /**
   * Get combined date and time string.
   */
  public getFormattedDateTime(): string {
    return `${this.getFormattedDate()}, ${this.getFormattedTime()}`;
  }

  /**
   * Set the current date/time directly (for loading saves or syncing).
   * @throws RangeError if any value is outside valid range
   */
  public setDateTime(dateTime: GameDateTime): void {
    const { year, month, day, hour, minute } = dateTime;

    if (!Number.isFinite(year)) {
      throw new RangeError(`Invalid year: ${year}`);
    }

    if (!Number.isInteger(month) || month < 1 || month > 12) {
      throw new RangeError(`Invalid month: ${month}`);
    }

    const maxDay = getDaysInMonth(year, month);
    if (!Number.isInteger(day) || day < 1 || day > maxDay) {
      throw new RangeError(`Invalid day: ${day} for ${year}-${month}`);
    }

    if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
      throw new RangeError(`Invalid hour: ${hour}`);
    }

    if (!Number.isInteger(minute) || minute < 0 || minute > 59) {
      throw new RangeError(`Invalid minute: ${minute}`);
    }

    this.year = year;
    this.month = month;
    this.day = day;
    this.hour = hour;
    this.minute = minute;
    this.tickAccumulator = 0;
  }

  /**
   * Reset to initial state (for testing).
   */
  public reset(): void {
    this.tickAccumulator = 0;
    this.year = STARTING_YEAR;
    this.month = STARTING_MONTH;
    this.day = STARTING_DAY;
    this.hour = STARTING_HOUR;
    this.minute = STARTING_MINUTE;
  }

  /**
   * Get ambient light level based on time of day.
   * Returns 0-15 where 0 is complete darkness and 15 is full daylight.
   *
   * Uses a smooth cosine wave:
   * - 12:00 (noon): Maximum brightness (15)
   * - 00:00 (midnight): Minimum brightness (0)
   * - 06:00 / 18:00: Half brightness (~7.5)
   */
  public getAmbientLightLevel(): number {
    return Math.round(this.getAmbientLightFloat() * 15);
  }

  /**
   * Get precise ambient light level as a float (0.0-1.0).
   * Useful for smooth lighting transitions.
   *
   * Uses a cosine wave centered on noon:
   * - 1.0 at 12:00 (noon)
   * - 0.0 at 00:00 (midnight)
   */
  public getAmbientLightFloat(): number {
    // Convert hour+minute to fractional hour (0-24)
    const time = this.hour + this.minute / 60;

    // Cosine wave: peaks at 12:00 (noon), minimum at 00:00 (midnight)
    // Map [-1, 1] to [0, 1]
    const radians = ((time - 12) / 12) * Math.PI;
    return (Math.cos(radians) + 1) / 2;
  }
}
